// 切れたときに張り直す方針（研究用リポジトリの DOC-0031 の M3 の写し。板 #193 で受領）。
//
// # なぜ要るか
//
// 接続が切れると、プラットフォーム層はこれまで退室していた（`main.ts` の `onClose` が `leave()` を
// 呼ぶだけ）。回線が切り替わる（Wi-Fi ⇄ モバイル）たびに通話が終わり、利用者は
// ボタンを押し直すことになる。**利用環境の前提はモバイル回線と企業内 LAN の混在**
// なので、切り替わりは必ず起きる。
//
// # 正本は `client/core/src/reconnect.rs`
//
// **ここは写しで、値と判断は core が正本**（ADR-0017 §5-3。最終的な主クライアントは
// ネイティブアプリなので、方針は Rust 側に残す）。
//
// 写しになっているのは、WASM が AudioWorklet の中にしか無く（`audio.ts`）、**入室前・
// 切断中はまだ Engine が無い**ため。この方針が要るのはまさにその瞬間なので、
// メインスレッドから core を呼べない。`worklet.ts` が発話検出の既定値を写しているのと
// 同じ扱いで、**ずれを試験で止める**（`test/reconnect.test.ts` が Rust 側の既定値を
// 読んで突き合わせる）。
//
// 待ち時間の表と、倍々・揺らぎ・「健康だった接続」の考え方は core 側の説明にある。

import type { Stage } from "./reachability.ts";

/** 切れ方。`Cause` の値は core の `reconnect::Cause` と 1 対 1。 */
export type Cause =
  /** 繋がっていたものが切れた（回線の切り替わり・サーバの再起動）。張り直す */
  | "transport"
  /** 繋ぎに行って失敗した（時間切れ・拒否）。張り直す */
  | "attempt"
  /** サーバが明示的に断った（満員・版違い）。待っても変わらない */
  | "rejected"
  /** この端末・この宛先では成立しない（経路が無い・URL の形・証明書） */
  | "unsupported"
  /** 自分の操作で閉じた */
  | "by-us";

/** 方針の定数の形。 */
export interface Policy {
  /** 何回まで張り直すか。使い切ったら利用者に任せる。 */
  maxAttempts: number;
  /** 1 回目の待ち時間（ms）。 */
  firstDelayMs: number;
  /** 待ち時間の上限（ms）。倍々にしてもここで止める。 */
  maxDelayMs: number;
  /** 待ち時間に入れる揺らぎの割合（0.0〜1.0）。 */
  jitter: number;
  /** これだけもった接続は「健康だった」とみなす（ms）。 */
  stableMs: number;
}

/** 方針の既定値。**core の `Policy::default` の写し**（ずれは試験で止める）。 */
export const POLICY: Policy = {
  /** 何回まで張り直すか。 */
  maxAttempts: 6,
  /** 1 回目の待ち時間（ms）。 */
  firstDelayMs: 500,
  /** 待ち時間の上限（ms）。 */
  maxDelayMs: 8000,
  /** 待ち時間に入れる揺らぎの割合（±25%）。 */
  jitter: 0.25,
  /** これだけもった接続は「健康だった」とみなす（ms）。 */
  stableMs: 15000,
};

export type Plan =
  | { kind: "retry"; attempt: number; delayMs: number }
  | { kind: "give-up"; attempts: number }
  | { kind: "stop" };

/** 時間をおけば直る見込みがあるか。 */
export function isRetryable(cause: Cause): boolean {
  return cause === "transport" || cause === "attempt";
}

/**
 * 接続の失敗（`reachability.classifyStage` の段階）を切れ方に直す。
 *
 * **`timeout` は張り直す**（UDP が捨てられている回線でも、テザリングに切り替えた
 * 瞬間に通るようになる）。**`cert` は張り直さない** — サーバには届いているので
 * 回線の問題ではなく、待っても証明書は直らない。
 *
 * **研究側の `Stage` には宛先の形の誤り（`url`）があり `unsupported` に寄せている**が、
 * 本番の `Stage` は 5 値（`reachability.ts`）。本番は招待 URL から入るので宛先を手で
 * 入れる段が無く、この値を持っていない。**足すなら `cert` と同じ側へ**（待っても直らない）。
 */
export function causeOfStage(stage: Stage): Cause {
  switch (stage) {
    case "unsupported":
    case "cert":
      return "unsupported";
    default:
      return "attempt";
  }
}

/** 張り直した回数を数えるだけの入れ物。`core` の `Reconnect` と同じ。 */
export class Reconnect {
  private count = 0;

  constructor(private readonly policy: Policy = POLICY) {}

  get attempts(): number {
    return this.count;
  }

  /** 入室まで行けたので忘れる。 */
  settled(): void {
    this.count = 0;
  }

  /**
   * 次の一手を決める。
   *
   * @param uptimeMs 直前の接続がもっていた時間。繋ぐ前に失敗したなら 0
   * @param rand01 0.0〜1.0。揺らぎに使う（試験では固定値を渡す）
   */
  plan(cause: Cause, uptimeMs: number, rand01: number = Math.random()): Plan {
    if (!isRetryable(cause)) {
      this.count = 0;
      return { kind: "stop" };
    }
    // 前の接続が十分もっていたなら、これは新しい故障
    if (uptimeMs >= this.policy.stableMs) this.count = 0;
    if (this.count >= this.policy.maxAttempts) {
      const attempts = this.count;
      this.count = 0;
      return { kind: "give-up", attempts };
    }
    this.count += 1;
    return { kind: "retry", attempt: this.count, delayMs: this.delayMs(this.count, rand01) };
  }

  /** `attempt` 回目（1 から）の待ち時間。倍々 → 上限で止める → 揺らぎを掛ける。 */
  private delayMs(attempt: number, rand01: number): number {
    let base = this.policy.firstDelayMs;
    for (let i = 1; i < attempt; i += 1) base = Math.min(base * 2, this.policy.maxDelayMs);
    const jitter = Math.min(Math.max(this.policy.jitter, 0), 1);
    // rand01 を −1.0〜+1.0 に広げて掛ける（0.5 でちょうど基準の待ち時間）
    const swing = (Math.min(Math.max(rand01, 0), 1) * 2 - 1) * jitter;
    return Math.trunc(Math.max(base * (1 + swing), 0));
  }
}
